import { useState } from "react";
import { api } from "../../lib/api";
import { FileText, BarChart3, Banknote, Award, Building2, CheckCircle2, Loader2, Landmark } from "lucide-react";

const DOCS = [
  { id: "bank_statements", label: "Bank statements (last 6 months)", Icon: Landmark },
  { id: "pitch_deck", label: "Pitch deck or company profile", Icon: FileText },
  { id: "mgmt_accounts", label: "Management accounts", Icon: BarChart3 },
  { id: "financial_model", label: "Financial model", Icon: Banknote },
  { id: "audited_financials", label: "Audited financials", Icon: Award },
  { id: "certificate", label: "Certificate of incorporation", Icon: Building2 },
];

// First-dashboard-visit checklist for post-revenue founders who completed the
// pre-sign-in quiz (the documents step moved here from the quiz).
export const FirstVisitDocsChecklist = ({ initial = [], onDone }) => {
  const [picked, setPicked] = useState(initial);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    setBusy(true);
    try { await api.post("/companies/me/quiz-docs", { docs: picked }); } catch { /* best effort */ }
    setBusy(false);
    onDone(picked);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center" data-testid="first-visit-docs-modal">
      <div className="w-full max-w-lg rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl sm:p-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">One quick question</p>
        <h2 className="mt-1 text-base font-extrabold text-neutral-900 md:text-lg">Which of these documents do you already have?</h2>
        <p className="mt-1 text-sm text-neutral-600">Tick what you have today — we'll tailor your upload checklist and tell you what Clinq can help you build.</p>
        <div className="mt-4 space-y-2">
          {DOCS.map(({ id, label, Icon }) => {
            const on = picked.includes(id);
            return (
              <button key={id} onClick={() => toggle(id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${on ? "border-[#0A8B85] bg-[#0A8B85]/5 text-neutral-900" : "border-neutral-200 text-neutral-700 hover:border-neutral-300"}`}
                data-testid={`first-visit-doc-${id}`}>
                <Icon className={`h-4 w-4 shrink-0 ${on ? "text-[#0A8B85]" : "text-neutral-400"}`} />
                <span className="flex-1 font-medium">{label}</span>
                {on && <CheckCircle2 className="h-4 w-4 text-[#0A8B85]" />}
              </button>
            );
          })}
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button onClick={() => { setPicked([]); save(); }} disabled={busy} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-neutral-500 hover:text-neutral-800" data-testid="first-visit-docs-none">
            I don't have any yet
          </button>
          <button onClick={save} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#087a74] disabled:opacity-50" data-testid="first-visit-docs-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continue
          </button>
        </div>
      </div>
    </div>
  );
};
